import { useCallback, useState } from 'react';
import { ActivityIndicator, Alert, Modal, Platform, RefreshControl, ScrollView, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import { BarChart, LineChart } from 'react-native-chart-kit';
import SectionCard from '../components/SectionCard';
import StatCard from '../components/StatCard';
import TransactionRow from '../components/TransactionRow';
import DateFilterBar from '../components/DateFilterBar';
import { fetchAnalytics, fetchTransactions, postDailyExpenseLimit } from '../lib/api';
import { currency, percent, shortDate, todayKey } from '../lib/format';

const chartConfig = {
  backgroundGradientFrom: '#ffffff',
  backgroundGradientTo: '#ffffff',
  decimalPlaces: 0,
  color: (opacity = 1) => `rgba(14, 116, 144, ${opacity})`,
  labelColor: (opacity = 1) => `rgba(71, 85, 105, ${opacity})`,
  propsForBackgroundLines: { stroke: '#e2e8f0' },
  propsForDots: { r: '4', strokeWidth: '2', stroke: '#0891b2' },
};

export default function HomeScreen({ navigation }) {
  const { width } = useWindowDimensions();
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ start: '', end: '' });
  const [appliedFilters, setAppliedFilters] = useState({ start: '', end: '' });
  const [limitModal, setLimitModal] = useState(false);
  const [dailyLimitAmount, setDailyLimitAmount] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  const showMessage = (title, message) => {
    if (Platform.OS === 'web') window.alert(`${title}\n\n${message}`);
    else Alert.alert(title, message);
  };

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const [analyticsData, txs] = await Promise.all([fetchAnalytics(appliedFilters), fetchTransactions(appliedFilters)]);
      setAnalytics(analyticsData);
      setTransactions(txs.slice(0, 8));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [appliedFilters]);

  useFocusEffect(useCallback(() => {
    load(false);
  }, [load]));

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center bg-slate-50">
        <ActivityIndicator size="large" color="#0891b2" />
        <Text className="mt-4 text-slate-600">Loading your money map...</Text>
      </View>
    );
  }

  const totals = analytics?.totals || {};
  const recentDays = (analytics?.dailySummary || []).slice(-7);
  const chartWidth = Math.max(280, Math.min(width - 48, 760));
  const chartLabels = recentDays.map((day) => shortDate(day.date));
  const expenseData = recentDays.map((day) => day.expense || 0);
  const balanceData = recentDays.map((day) => day.balanceLeft || 0);
  const highest = analytics?.highestSpendingDay;
  const strictSavingsDue = analytics?.financialHealth?.strictSavingsDue || [];
  const dailyLimit = analytics?.dailyExpenseLimit || null;

  const openLimitModal = () => {
    setDailyLimitAmount(dailyLimit?.amount ? String(dailyLimit.amount) : '');
    setLimitModal(true);
  };

  const saveDailyLimit = async () => {
    setSavingLimit(true);
    try {
      await postDailyExpenseLimit({ amount: dailyLimitAmount, start_date: todayKey() });
      setLimitModal(false);
      await load(true);
    } catch (err) {
      showMessage('Could not save daily maximum', err.message);
    } finally {
      setSavingLimit(false);
    }
  };

  return (
    <ScrollView
      className="flex-1 bg-slate-50"
      contentContainerStyle={{ padding: 16, paddingBottom: 36 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
    >
      <View className="rounded-2xl bg-slate-950 p-5">
        <View className="flex-row items-start justify-between gap-3">
          <View className="flex-1">
            <Text className="text-sm font-semibold uppercase text-cyan-200">Daily balance tracker</Text>
            <Text className="mt-2 text-3xl font-black text-white">Your money, mapped daily.</Text>
            <Text className="mt-3 text-base text-slate-300">
              Review today, record what changed, and keep your plans in view.
            </Text>
          </View>
          <TouchableOpacity className="h-11 w-11 items-center justify-center rounded-full bg-white" onPress={() => navigation.navigate('AddTransaction')}>
            <MaterialIcons name="add" size={24} color="#0f172a" />
          </TouchableOpacity>
        </View>
        <View className="mt-5 rounded-2xl bg-white/10 p-4">
          <Text className="text-sm text-slate-300">Current balance</Text>
          <Text className="mt-1 text-4xl font-black text-white">{currency(totals.currentBalance)}</Text>
          <Text className="mt-2 text-sm text-slate-300">
            {percent(totals.spendRatio)} of income spent • {currency(totals.averageDailyExpense)} average daily spend
          </Text>
        </View>
      </View>

      {error ? (
        <View className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <Text className="font-semibold text-rose-700">{error}</Text>
        </View>
      ) : null}

      {strictSavingsDue.length ? (
        <View className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <View className="flex-row items-start gap-3">
            <View className="h-10 w-10 items-center justify-center rounded-full bg-rose-700">
              <MaterialIcons name="notifications-active" size={22} color="#ffffff" />
            </View>
            <View className="flex-1">
              <Text className="text-base font-black text-rose-900">Strict savings due</Text>
              {strictSavingsDue.slice(0, 3).map((goal) => (
                <View key={goal.id} className="mt-2">
                  <Text className="font-bold text-rose-800">{goal.name}</Text>
                  <Text className="text-sm text-rose-700">
                    Save {goal.per_period_required === null ? currency(goal.remaining_amount) : currency(goal.per_period_required)} for this {goal.contribution_frequency} plan. Due {goal.next_due_date}.
                  </Text>
                </View>
              ))}
              <TouchableOpacity
                className="mt-3 rounded-2xl bg-rose-700 px-4 py-3"
                onPress={() => navigation.navigate('SavingsGoals', { openModal: 'savingRecord', planId: String(strictSavingsDue[0]?.id || '') })}
              >
                <Text className="text-center font-bold text-white">Record savings payment</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : null}

      <View className={`mt-4 rounded-2xl border p-4 ${dailyLimit?.todayOverLimit ? 'border-rose-200 bg-rose-50' : 'border-cyan-200 bg-cyan-50'}`}>
        <View className="flex-row items-start justify-between gap-3">
          <View className="flex-1">
            <Text className={`text-base font-black ${dailyLimit?.todayOverLimit ? 'text-rose-900' : 'text-cyan-950'}`}>Daily maximum expense</Text>
            {dailyLimit ? (
              <>
                <Text className={`mt-1 text-sm ${dailyLimit.todayOverLimit ? 'text-rose-700' : 'text-cyan-800'}`}>
                  Today: {currency(dailyLimit.todaySpent)} of {currency(dailyLimit.amount)} actual spending. Planned expenses are not counted.
                </Text>
                <View className="mt-3 h-2 overflow-hidden rounded-full bg-white">
                  <View className={`h-2 rounded-full ${dailyLimit.todayOverLimit ? 'bg-rose-600' : 'bg-cyan-700'}`} style={{ width: `${Math.min(dailyLimit.todayPercent, 100)}%` }} />
                </View>
                <Text className={`mt-2 text-sm font-bold ${dailyLimit.todayOverLimit ? 'text-rose-700' : 'text-cyan-800'}`}>
                  {dailyLimit.todayOverLimit ? `${currency(dailyLimit.todaySpent - dailyLimit.amount)} over today's maximum` : `${currency(dailyLimit.todayRemaining)} left for today`}
                </Text>
              </>
            ) : (
              <Text className="mt-1 text-sm text-cyan-800">Set a maximum amount you want actual daily expenses to stay under.</Text>
            )}
          </View>
          <TouchableOpacity className="rounded-2xl bg-slate-950 px-4 py-3" onPress={openLimitModal}>
            <Text className="font-bold text-white">{dailyLimit ? 'Edit' : 'Set'}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View className="mt-4">
        <DateFilterBar
          filters={filters}
          onChange={setFilters}
          onApply={() => setAppliedFilters(filters)}
          onClear={() => {
            setFilters({ start: '', end: '' });
            setAppliedFilters({ start: '', end: '' });
          }}
        />
      </View>

      <View className="mt-4 flex-row flex-wrap gap-3">
        <View className="min-w-[150px] flex-1">
          <StatCard label="Income" value={currency(totals.totalIncome)} subtitle="All money made" icon="payments" tone="green" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Spending" value={currency(totals.totalExpense)} subtitle="All expenses" icon="shopping-cart" tone="red" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Savings rate" value={percent(totals.savingsRate)} subtitle="Income kept" icon="savings" tone="indigo" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Runway" value={totals.burnRateDays === null ? 'N/A' : `${totals.burnRateDays}d`} subtitle="At current spend" icon="timeline" tone="amber" />
        </View>
      </View>

      <View className="mt-4 flex-row gap-3">
        <TouchableOpacity className="flex-1 rounded-2xl bg-cyan-700 p-4" onPress={() => navigation.navigate('AddTransaction')}>
          <MaterialIcons name="add-card" size={24} color="#ffffff" />
          <Text className="mt-3 text-base font-bold text-white">Add income or expense</Text>
        </TouchableOpacity>
        <TouchableOpacity className="flex-1 rounded-2xl bg-slate-900 p-4" onPress={() => navigation.navigate('Analytics')}>
          <MaterialIcons name="query-stats" size={24} color="#ffffff" />
          <Text className="mt-3 text-base font-bold text-white">Open full analysis</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity className="mt-3 rounded-2xl bg-indigo-700 p-4" onPress={() => navigation.navigate('Planning')}>
        <View className="flex-row items-center justify-between">
          <View className="flex-1">
            <Text className="text-base font-bold text-white">Savings, recurring bills, and cut plans</Text>
            <Text className="mt-1 text-sm text-indigo-100">
              {analytics?.financialHealth?.savingsPlans?.length || 0} achievement goal(s) • {currency(analytics?.financialHealth?.savingsTotal || 0)} saved
            </Text>
          </View>
          <MaterialIcons name="arrow-forward" size={24} color="#ffffff" />
        </View>
      </TouchableOpacity>

      <View className="mt-4">
        <SectionCard title="Planning Shortcuts" subtitle="Create plans and allocate funds directly from here.">
          <View className="flex-row flex-wrap gap-3">
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-indigo-700 p-4" onPress={() => navigation.navigate('SavingsGoals')}>
              <MaterialIcons name="flag" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Create saving goal</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-rose-700 p-4" onPress={() => navigation.navigate('Expenses')}>
              <MaterialIcons name="priority-high" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Expenses</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-fuchsia-700 p-4" onPress={() => navigation.navigate('Wishlist')}>
              <MaterialIcons name="redeem" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Wishlist</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-cyan-700 p-4" onPress={() => navigation.navigate('FundPartitions')}>
              <MaterialIcons name="account-tree" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Allocate funds</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-amber-700 p-4" onPress={() => navigation.navigate('RecurringExpenses')}>
              <MaterialIcons name="event-repeat" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Recurring expenses</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-slate-700 p-4" onPress={() => navigation.navigate('CutSuggestions')}>
              <MaterialIcons name="content-cut" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Cut suggestions</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-slate-900 p-4" onPress={() => navigation.navigate('Planning')}>
              <MaterialIcons name="dashboard-customize" size={24} color="#ffffff" />
              <Text className="mt-3 font-bold text-white">Open planning center</Text>
            </TouchableOpacity>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Balance Line" subtitle="Closing balance after income, spending, and brought-forward cash.">
          {recentDays.length ? (
            <LineChart
              data={{ labels: chartLabels, datasets: [{ data: balanceData }] }}
              width={chartWidth}
              height={220}
              yAxisLabel="$"
              chartConfig={chartConfig}
              bezier
              style={{ borderRadius: 12, marginLeft: -12 }}
            />
          ) : <Text className="text-slate-500">Add your first transaction to see the balance line.</Text>}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Daily Spending" subtitle={highest ? `Most expensive day: ${shortDate(highest.date)} at ${currency(highest.expense)}.` : 'Your highest-spend day will appear here.'}>
          {recentDays.length ? (
            <BarChart
              data={{ labels: chartLabels, datasets: [{ data: expenseData }] }}
              width={chartWidth}
              height={220}
              yAxisLabel="$"
              fromZero
              showValuesOnTopOfBars
              chartConfig={{ ...chartConfig, color: (opacity = 1) => `rgba(225, 29, 72, ${opacity})` }}
              style={{ borderRadius: 12, marginLeft: -12 }}
            />
          ) : <Text className="text-slate-500">No spending recorded yet.</Text>}
        </SectionCard>
      </View>

      {dailyLimit ? (
        <View className="mt-4">
          <SectionCard title="Daily Maximum Review" subtitle="Actual expenses only. Planned expenses and forecasts are excluded.">
            {recentDays.map((day) => (
              <View key={day.date} className="mb-3 rounded-2xl bg-slate-50 p-4">
                <View className="flex-row items-center justify-between">
                  <Text className="font-black text-slate-950">{shortDate(day.date)}</Text>
                  <Text className={`font-black ${day.overDailyMax ? 'text-rose-600' : 'text-cyan-700'}`}>{currency(day.expense)}</Text>
                </View>
                <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                  <View className={`h-2 rounded-full ${day.overDailyMax ? 'bg-rose-600' : 'bg-cyan-700'}`} style={{ width: `${Math.min(day.dailyMaxPercent || 0, 100)}%` }} />
                </View>
                <Text className="mt-2 text-sm text-slate-600">
                  {day.overDailyMax ? `${currency(day.overLimitAmount)} over maximum` : `${currency(day.dailyMaxRemaining)} remaining`}
                </Text>
              </View>
            ))}
          </SectionCard>
        </View>
      ) : null}

      <View className="mt-4">
        <SectionCard title="Top Money Consumers" subtitle="Activities and categories taking the biggest share.">
          {(analytics?.topCategories || []).slice(0, 5).map((category) => (
            <View key={category.category} className="mb-3">
              <View className="flex-row items-center justify-between">
                <Text className="font-bold text-slate-900">{category.category}</Text>
                <Text className="font-bold text-rose-600">{currency(category.expense)}</Text>
              </View>
              <View className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <View className="h-2 rounded-full bg-rose-500" style={{ width: `${Math.min(category.shareOfSpend, 100)}%` }} />
              </View>
              <Text className="mt-1 text-xs text-slate-500">{percent(category.shareOfSpend)} of total spending</Text>
            </View>
          ))}
          {!analytics?.topCategories?.length ? <Text className="text-slate-500">No expense categories yet.</Text> : null}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Financial Achievement Goals" subtitle="Things you are saving to achieve, buy, or fund.">
          {(analytics?.financialHealth?.savingsPlans || []).slice(0, 4).map((goal) => (
            <View key={goal.id} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-1">
                  <Text className="font-bold text-slate-950">{goal.name}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{goal.deadline_label} • {currency(goal.remaining_amount)} left</Text>
                </View>
                <MaterialIcons name={goal.achieved ? 'emoji-events' : 'flag'} size={24} color={goal.achieved ? '#d97706' : '#4f46e5'} />
              </View>
              <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <View className="h-2 rounded-full bg-indigo-600" style={{ width: `${Math.min(goal.progress_percent, 100)}%` }} />
              </View>
            </View>
          ))}
          {!analytics?.financialHealth?.savingsPlans?.length ? (
            <Text className="text-slate-500">Create a goal like laptop, rent buffer, emergency fund, or school fees.</Text>
          ) : null}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Money Milestones" subtitle="Automatic behavior milestones from your ledger.">
          {(analytics?.financialHealth?.achievements || []).slice(0, 3).map((achievement) => (
            <View key={achievement.key} className="mb-3 flex-row items-center justify-between rounded-2xl bg-slate-50 p-4">
              <View className="flex-1">
                <Text className="font-bold text-slate-950">{achievement.title}</Text>
                <Text className="mt-1 text-sm text-slate-500">{achievement.progress.toFixed ? achievement.progress.toFixed(1) : achievement.progress} / {achievement.target}</Text>
              </View>
              <MaterialIcons name={achievement.achieved ? 'emoji-events' : 'radio-button-unchecked'} size={24} color={achievement.achieved ? '#d97706' : '#94a3b8'} />
            </View>
          ))}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Recent Ledger" subtitle={`${analytics?.transactionCount || 0} total transaction(s) recorded.`}>
          {transactions.length ? transactions.map((tx) => <TransactionRow key={tx.id} transaction={tx} />) : (
            <Text className="text-slate-500">Start by recording what you made or spent today.</Text>
          )}
        </SectionCard>
      </View>

      <Modal visible={limitModal} transparent animationType="fade" onRequestClose={() => setLimitModal(false)}>
        <View className="flex-1 justify-center bg-black/40 p-4">
          <View className="rounded-2xl bg-white p-5">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="text-xl font-black text-slate-950">Daily maximum expense</Text>
              <TouchableOpacity onPress={() => setLimitModal(false)}>
                <MaterialIcons name="close" size={24} color="#0f172a" />
              </TouchableOpacity>
            </View>
            <Text className="mb-2 text-sm font-semibold text-slate-500">Actual recorded expenses only. Planned expenses are not included.</Text>
            <TextInput
              className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950"
              placeholder="Maximum per day"
              placeholderTextColor="#94a3b8"
              keyboardType="decimal-pad"
              value={dailyLimitAmount}
              onChangeText={setDailyLimitAmount}
            />
            <TouchableOpacity className="rounded-2xl bg-cyan-700 px-4 py-3" disabled={savingLimit} onPress={saveDailyLimit}>
              {savingLimit ? <ActivityIndicator color="#ffffff" /> : <Text className="text-center font-bold text-white">Save daily maximum</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}
